import {useState} from 'react'
import dayjs from 'dayjs'
import {useTranslation} from '@/translations/i18n.tsx'
import {ChevronLeftIcon, ChevronRightIcon} from '@/components/ui/icon/Icon'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {HISTORY_PAGE_SIZE} from '@/features/mailer/constants'
import {MailBatchSummary} from '@/features/mailer/types'
import {deriveBatchStatus} from '@/features/mailer/utils'
import {useMailBatches} from '@/features/mailer/queries/useMailBatches'
import {MailerBatchDetailsModal} from '@/features/mailer/components/mailer-batch-details-modal/MailerBatchDetailsModal'
import {
    StyledBatchCard,
    StyledBatchCardAside,
    StyledBatchCardMain,
    StyledBatchList,
    StyledHistoryWrapper,
    StyledOpensPlaceholder,
    StyledPaginationBar,
    StyledStatusPill
} from '@/features/mailer/components/mailer-history-tab/style'

const STATUS_LABEL_KEYS = {
    sent: 'mailer:history_status_sent',
    partial: 'mailer:history_status_partial',
    sending: 'mailer:history_status_sending'
} as const

// History tab (spec §10): clickable card list with synthetic status derived
// FE-side from batch counts. The opens metric is BE-gated (OPSSTD-3643 §7)
// and shown as a placeholder with tooltip.
export const MailerHistoryTab = () => {
    const {t} = useTranslation()
    const [page, setPage] = useState(1)
    const [selectedBatch, setSelectedBatch] = useState<MailBatchSummary | null>(null)

    const batchesQuery = useMailBatches({page, limit: HISTORY_PAGE_SIZE})
    const batches = batchesQuery.data?.data ?? []
    const meta = batchesQuery.data?.meta

    return (
        <StyledHistoryWrapper>
            {batchesQuery.isLoading ? (
                <Flexbox justify="center" style={{padding: 32}}>
                    <Spinner size={24} />
                </Flexbox>
            ) : batches.length === 0 ? (
                <EmptyState title={t('mailer:history_empty_title')} subtitle={t('mailer:history_empty_description')} />
            ) : (
                <>
                    <StyledBatchList>
                        {batches.map(batch => {
                            const status = deriveBatchStatus(batch)
                            return (
                                <StyledBatchCard
                                    key={batch.batchId}
                                    type="button"
                                    onClick={() => setSelectedBatch(batch)}
                                >
                                    <StyledBatchCardMain>
                                        <small>{dayjs(batch.createdAt).format('DD/MM/YYYY HH:mm')}</small>
                                        <strong>{batch.subject}</strong>
                                        <span>
                                            {t('mailer:history_recipients', {count: batch.totalRecipients})}
                                            {' · '}
                                            <Tooltip
                                                content={t('mailer:not_available_yet')}
                                                triggerProps={{asChild: true}}
                                            >
                                                <StyledOpensPlaceholder>
                                                    {t('mailer:history_opens_placeholder')}
                                                </StyledOpensPlaceholder>
                                            </Tooltip>
                                            {batch.triggeredBy &&
                                                ` · ${t('mailer:history_triggered_by', {
                                                    name: `${batch.triggeredBy.firstName} ${batch.triggeredBy.lastName}`
                                                })}`}
                                        </span>
                                    </StyledBatchCardMain>
                                    <StyledBatchCardAside>
                                        <StyledStatusPill $status={status}>
                                            {t(STATUS_LABEL_KEYS[status])}
                                        </StyledStatusPill>
                                        <ChevronRightIcon />
                                    </StyledBatchCardAside>
                                </StyledBatchCard>
                            )
                        })}
                    </StyledBatchList>

                    {meta && meta.lastPage > 1 && (
                        <StyledPaginationBar>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label={t('mailer:pagination_prev')}
                                disabled={page <= 1}
                                onClick={() => setPage(previous => Math.max(1, previous - 1))}
                            >
                                <ChevronLeftIcon />
                            </Button>
                            <span>
                                {t('mailer:batch_detail_pagination_label', {
                                    current: meta.currentPage,
                                    total: meta.lastPage
                                })}
                            </span>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                shape="square"
                                aria-label={t('mailer:pagination_next')}
                                disabled={page >= meta.lastPage}
                                onClick={() => setPage(previous => previous + 1)}
                            >
                                <ChevronRightIcon />
                            </Button>
                        </StyledPaginationBar>
                    )}
                </>
            )}

            {selectedBatch && <MailerBatchDetailsModal batch={selectedBatch} onClose={() => setSelectedBatch(null)} />}
        </StyledHistoryWrapper>
    )
}

MailerHistoryTab.displayName = 'MailerHistoryTab'
